import { useEffect, useState } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import {
  useCreatePaymentRequestMutation,
  useScholarshipAwardQuery,
  type ScholarshipAwardListItem,
} from "../../lib/api/scholarships";
import { formatMoney } from "../../lib/format/money";
import { expenseStatusTone, formatExpenseStatus } from "../../lib/format/status";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Modal } from "../../components/ui/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";

interface ScholarshipDetailModalProps {
  award: ScholarshipAwardListItem | null;
  onClose: () => void;
  canRequestPayment: boolean;
}

function unrequestedAmount(award: ScholarshipAwardListItem): string {
  const allocated = Number.parseFloat(award.allocatedAmount);
  const committed = Number.parseFloat(award.committedAmount);
  return Math.max(0, allocated - committed).toFixed(2);
}

export function ScholarshipDetailModal({
  award,
  onClose,
  canRequestPayment,
}: ScholarshipDetailModalProps) {
  const detail = useScholarshipAwardQuery(award?.id ?? null);
  const createPaymentRequest = useCreatePaymentRequestMutation(award?.id ?? "");
  const { defaultDate } = useFiscalDefaults();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState("");
  const [missingEvidenceReason, setMissingEvidenceReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canRequest =
    Boolean(award) &&
    canRequestPayment &&
    Number.parseFloat(unrequestedAmount(award!)) > 0;

  useEffect(() => {
    if (showRequestForm && defaultDate) {
      setExpenseDate(defaultDate);
    }
  }, [showRequestForm, defaultDate]);

  async function submitPaymentRequest() {
    if (!award) {
      return;
    }
    setError(null);
    try {
      await createPaymentRequest.mutateAsync({
        amount: Number.parseFloat(amount).toFixed(2),
        expenseDate,
        missingEvidenceReason: missingEvidenceReason || undefined,
        submit: true,
      });
      setAmount("");
      setShowRequestForm(false);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not create payment request.");
    }
  }

  return (
    <Modal
      open={Boolean(award)}
      onClose={onClose}
      title={award?.studentName ?? "Scholarship award"}
      description={
        award ? `${award.institution} · ${award.period}` : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {canRequest ? (
            <Button
              onClick={() => {
                setShowRequestForm(true);
              }}
            >
              Request payment
            </Button>
          ) : null}
        </>
      }
    >
      {detail.isLoading ? (
        <LoadingSpinner label="Loading award…" className="py-10" />
      ) : detail.data && award ? (
        <div className="grid gap-6">
          <div className="grid gap-4 rounded-[11px] bg-[#f5f8f6] p-[18px] sm:grid-cols-3">
            <div>
              <small className="block text-xs text-[#84908d]">Allocated</small>
              <b className="mt-1.5 block text-base font-semibold tracking-[-0.3px] text-edlab-ink">
                {formatMoney(award.allocatedAmount)}
              </b>
            </div>
            <div>
              <small className="block text-xs text-[#84908d]">Paid</small>
              <b className="mt-1.5 block text-base font-semibold tracking-[-0.3px] text-edlab-ink">
                {formatMoney(award.paidAmount)}
              </b>
            </div>
            <div>
              <small className="block text-xs text-[#84908d]">Unrequested</small>
              <b className="mt-1.5 block text-base font-semibold tracking-[-0.3px] text-edlab-ink">
                {formatMoney(unrequestedAmount(award))}
              </b>
            </div>
          </div>

          <p className="text-sm text-edlab-muted">{award.fundName}</p>

          {showRequestForm && canRequestPayment ? (
            <section className="rounded-xl border border-edlab-line bg-edlab-bg/40 p-4">
              <h3 className="mb-3 text-sm font-semibold text-edlab-ink">Request payment</h3>
              {error ? <p className="edlab-error mb-3">{error}</p> : null}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  Request amount · PKR
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="edlab-input"
                    value={amount}
                    onChange={(event) => {
                      setAmount(event.target.value);
                    }}
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  Request date
                  <input
                    type="date"
                    className="edlab-input"
                    value={expenseDate}
                    onChange={(event) => {
                      setExpenseDate(event.target.value);
                    }}
                  />
                </label>
                <label className="grid gap-1 text-sm sm:col-span-2">
                  Missing evidence reason
                  <input
                    className="edlab-input"
                    value={missingEvidenceReason}
                    onChange={(event) => {
                      setMissingEvidenceReason(event.target.value);
                    }}
                    placeholder="Institution invoice to be attached"
                  />
                </label>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  disabled={createPaymentRequest.isPending}
                  onClick={() => void submitPaymentRequest()}
                >
                  Submit request
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowRequestForm(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </section>
          ) : null}

          {detail.data.award.paymentRequests.length ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-edlab-ink">Payment requests</h3>
              <Table caption="Scholarship payment requests">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Expense</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell className="text-right">Requested</TableHeaderCell>
                    <TableHeaderCell className="text-right">Paid</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.data.award.paymentRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{request.expense.referenceNo}</TableCell>
                      <TableCell>
                        <Badge tone={expenseStatusTone(request.expense.status)}>
                          {formatExpenseStatus(request.expense.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(request.requestedAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(request.expense.paidAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          ) : (
            <p className="text-sm text-edlab-muted">
              No payment requested yet. Create a request when the tuition payment is due.
            </p>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
